// The guest warning before the season roll (deployment.md §10.3, R607,
// R1565).
//
// The meta server deletes a guest account that nobody made their own at
// the instant the core season rolls (R607). The sweep reads four facts of
// the account: it is a guest, it holds no sign-in method, its free rename
// is unspent, and it was made before the closing season began. `/api/me`
// carries all four (`kind`, `identities`, `canRename`, `createdAt`), so
// this client states the rule from facts it already has and needs no new
// route.
//
// Two surfaces state it:
//
//   - The card on the map. It opens one time a season, when seaquake
//     stage 1 opens in a Conquest world (numbers.md §15): the scoreboard
//     names the stage. It reads `/api/me` again at that moment, so a guest
//     that registered on the hub before it joined reads no false warning.
//     REGISTER takes the world down and opens the hub on its SETTINGS tab,
//     where the ACCOUNT card links a sign-in method. LATER puts it away.
//   - The card on the hub PLAY panel (`ui/hub/panels/play.ts`). The hub
//     opens no world socket, so it works the stage out from the soonest
//     season end a Conquest world states and the DESIGNED share and length
//     of the season (`QUAKE_STAGE1_FRAC`, `SEASON_DAYS`). It stands while
//     the condition holds, and it has no LATER.
//
// The card on the map and the explainer card under the top bar (the
// world clock card and the other bar cards) take the same slot at the top
// of the map, so the two never stand together (R2139). The guest warning
// asks for an act, so it wins when it rises: it closes the explainer card.
// A player who opens the explainer card while the warning stands reads it
// alone: the warning yields while it stands and comes back when it goes.
//
// The season start is worked out as the season end less one season at the
// world's timescale. A trimmed first season began later than that instant,
// so a guest made inside it is never warned, which is the sweep's own
// answer: the first season of a line sweeps nobody.

import { MODE_CORE, QUAKE_STAGE1_FRAC, SEASON_DAYS } from '../gen/balance';
import { guestWarn as S } from '../gen/strings';
import type { Me, WorldInfo } from '../net/api';
import type { GameState } from '../state';
import { readStore, writeStore } from './localstore';
import { SEC_PER_DAY } from '../locale/clock';

/** The four facts of `/api/me` that the sweep of R607 reads. */
export interface GuestFacts {
  /** `guest` or `registered`. */
  kind: string;
  /** How many sign-in methods the account holds. */
  identities: number;
  /** True while the free rename is unspent (`renamed_at IS NULL`). */
  canRename: boolean;
  /** When the account was made, in unix seconds. */
  createdAt: number;
}

/** The four facts, off one answer of `/api/me`. */
export function guestFacts(me: Me): GuestFacts {
  return {
    kind: me.account.kind,
    identities: me.identities?.length ?? 0,
    canRename: me.account.canRename,
    createdAt: me.account.createdAt,
  };
}

/**
 * True when the sweep at the roll deletes this account. It is the WHERE
 * clause of `dbrepo.SweepGuests`: a guest, no sign-in method, no rename,
 * and made before the closing season began.
 */
export function sweptAtRoll(f: GuestFacts, seasonBeganSec: number): boolean {
  return f.kind === 'guest' && f.identities === 0 && f.canRename && f.createdAt < seasonBeganSec;
}

/** The wall instant the running season began: the end less one season,
 *  at the world's timescale. */
export function seasonBeganSec(endSec: number, timescale: number): number {
  const scale = timescale > 0 ? timescale : 1;
  return endSec - (SEASON_DAYS * SEC_PER_DAY) / scale;
}

/** What the map page knows about its world. */
export interface WorldNow {
  /** The world's mode word (`core`, `event`, ...). Empty in direct mode. */
  mode: string;
  /** The seaquake stage of the scoreboard, 0 to 3. */
  stage: number;
  /** The hello's season end, in unix seconds on the wall clock. */
  seasonEndsSec: number;
  /** The world clock's multiplier. */
  timescale: number;
}

/** The map page: true when the card is due in this world now. */
export function warnInWorld(f: GuestFacts, w: WorldNow): boolean {
  return w.stage >= 1 && rollDeletes(f, w);
}

/**
 * True when the roll of this world deletes this account: a Conquest
 * world, and the sweep of R607 finds the account. The season summary card
 * asks it while the scores stand locked, so it reads no stage (R2089).
 */
export function rollDeletes(f: GuestFacts, w: Omit<WorldNow, 'stage'>): boolean {
  if (w.mode !== MODE_CORE) return false;
  return sweptAtRoll(f, seasonBeganSec(w.seasonEndsSec, w.timescale));
}

/**
 * The hub: the instant of the next core roll, which is the soonest season
 * end a Conquest world states after `now`, or 0 when none states one. Every
 * world of one line rolls on one instant, and the first core line to roll
 * sweeps the guest.
 */
export function nextCoreRoll(worlds: readonly WorldInfo[], now: number): number {
  let best = 0;
  for (const w of worlds) {
    const end = w.seasonEndAt ?? 0;
    if (w.mode !== MODE_CORE || end <= now) continue;
    if (best === 0 || end < best) best = end;
  }
  return best;
}

/**
 * The hub: true when seaquake stage 1 is open before the roll at
 * `rollSec`, at the designed pace, and that roll deletes this account.
 */
export function warnOnHub(f: GuestFacts, rollSec: number, now: number): boolean {
  if (rollSec <= now) return false;
  const season = SEASON_DAYS * SEC_PER_DAY;
  if (rollSec - now > QUAKE_STAGE1_FRAC * season) return false;
  return sweptAtRoll(f, rollSec - season);
}

/** The storage key of the last season the card opened in. */
export const GUEST_WARN_KEY = 'aquaphobia.guestWarnSeen';

/** One season of one account in one world: the card opens one time for it. */
export function seasonKey(accountId: number, worldId: number, season: number): string {
  return `${accountId}:${worldId}:${season}`;
}

/** The card's markup. Pure. */
function guestWarnMarkup(): string {
  return `<div class="head" id="guestwarn-head">${S.head}</div>`
    + `<p>${S.body}</p>`
    + `<footer><button type="button" data-gw="later">${S.later}</button>`
    + `<button type="button" class="primary" data-gw="register">${S.register}</button></footer>`;
}

/** What the card needs from the map page. */
export interface GuestWarnDeps {
  /** The chrome root `#ui`: the card stands in its stacking context. */
  host: HTMLElement;
  state: GameState;
  /** The world's mode word, which the join answer names. */
  mode: () => string;
  /** Reads `/api/me` again when the stage opens. */
  getMe: () => Promise<Me>;
  /** REGISTER: the world goes down and the hub opens on SETTINGS. */
  register: () => void;
  /** The explainer card under the top bar, which holds the same slot
   *  (R2139). Absent on a page with no top bar. */
  barTip?: BarTipSlot;
}

/** The two seams of the explainer card under the top bar (`Overlay`). */
export interface BarTipSlot {
  /** Hears each open and each close of the card. */
  onBarTip(fn: (open: boolean) => void): void;
  /** Closes the card. */
  closeBarTip(): void;
}

/** The card on the map. It opens itself, one time a season. */
export class GuestWarnCard {
  private el: HTMLElement | null = null;
  /** The season this page already asked about, so one stage asks once. */
  private asked = '';
  /** True while the explainer card stands, so the card yields (R2139). */
  private barTipOpen = false;

  constructor(private readonly d: GuestWarnDeps) {
    d.state.on(() => this.sync());
    d.barTip?.onBarTip((open) => this.yieldTo(open));
  }

  /** Presentation-only; the tests read it. */
  get showing(): boolean { return this.el !== null; }

  /** The card yields while the explainer card stands (R2139). */
  private yieldTo(open: boolean): void {
    this.barTipOpen = open;
    if (this.el) this.el.hidden = open;
  }

  private sync(): void {
    const s = this.d.state;
    const h = s.hello;
    if (h === null || this.el !== null) return;
    const mode = this.d.mode();
    const stage = s.board?.quakeStage ?? 0;
    if (mode !== MODE_CORE || stage < 1) return;
    const key = seasonKey(h.accountId, h.worldId, h.season);
    if (this.asked === key) return;
    this.asked = key;
    if (readStore(GUEST_WARN_KEY) === key) return;
    void this.check(key, { mode, stage, seasonEndsSec: Number(h.seasonEndsUnix), timescale: s.timescale });
  }

  private async check(key: string, world: WorldNow): Promise<void> {
    let me: Me;
    try {
      me = await this.d.getMe();
    } catch {
      return; // No answer, no claim: the hub card states the rule as well.
    }
    if (!warnInWorld(guestFacts(me), world) || this.el !== null) return;
    writeStore(GUEST_WARN_KEY, key);
    this.open();
  }

  private open(): void {
    // The warning asks for an act, so it takes the slot (R2139).
    this.d.barTip?.closeBarTip();
    const root = document.createElement('div');
    root.id = 'guestwarn';
    root.className = 'panel';
    root.setAttribute('role', 'alertdialog');
    root.setAttribute('aria-labelledby', 'guestwarn-head');
    root.innerHTML = guestWarnMarkup();
    root.hidden = this.barTipOpen;
    // Fixed chrome acts on the press (ui-map-interface.md §16.8, R1206).
    root.addEventListener('pointerdown', this.onPress);
    // A keyboard press arrives as a click with `detail` 0.
    root.addEventListener('click', this.onKeyClick);
    this.d.host.appendChild(root);
    this.el = root;
  }

  private close(): void {
    this.el?.remove();
    this.el = null;
  }

  private act(b: HTMLButtonElement): void {
    if (b.dataset.gw === 'register') this.d.register();
    this.close();
  }

  private readonly onPress = (ev: PointerEvent): void => {
    if (ev.button > 0) return;
    const b = cardButton(ev.target);
    if (!b) return;
    ev.preventDefault();
    this.act(b);
  };

  private readonly onKeyClick = (ev: MouseEvent): void => {
    if (ev.detail !== 0) return;
    const b = cardButton(ev.target);
    if (b) this.act(b);
  };
}

/** The button of the card an event target stands in, or null. */
function cardButton(t: EventTarget | null): HTMLButtonElement | null {
  const b = (t as Element | null)?.closest?.('button[data-gw]');
  return b instanceof HTMLButtonElement ? b : null;
}
